import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { X, MessageCircle, Trash2, LogOut, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/constants';

const formatDateTime = (dateString) => {
  const date = new Date(dateString);
  const dateStr = date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  const timeStr = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${dateStr} ${timeStr}`;
};

// Moved outside to avoid re-creation on each render
const MAX_REPLY_DEPTH = 2; // Original comment (0) + 1 reply level (1) = max depth of 2

// Rating Card Component - with reply support
const RatingCard = ({ 
  rating, 
  onNavigate, 
  token, 
  user,
  replyingTo, 
  replyText, 
  setReplyingTo, 
  setReplyText, 
  onAddComment,
  onDeleteComment,
  replies = []
}) => (
  <div className="bg-gradient-to-r from-amber-500/10 to-amber-600/5 border border-amber-500/20 rounded-xl p-4 mb-2">
    <div className="flex items-start gap-3">
      {/* Avatar */}
      {rating.profile_pic ? (
        <img 
          src={rating.profile_pic} 
          alt={rating.username}
          className="w-10 h-10 rounded-full object-cover flex-shrink-0 cursor-pointer hover:ring-2 hover:ring-amber-500 transition-all"
          onClick={() => onNavigate(`/user/${rating.user_id}`)}
        />
      ) : (
        <div 
          className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center text-white font-bold flex-shrink-0 cursor-pointer hover:ring-2 hover:ring-amber-500 transition-all"
          onClick={() => onNavigate(`/user/${rating.user_id}`)}
        >
          {rating.username?.charAt(0).toUpperCase() || 'U'}
        </div>
      )}
      
      {/* Content */}
      <div className="flex-1 min-w-0 overflow-hidden">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mb-1">
          <button 
            onClick={(e) => {
              e.stopPropagation();
              onNavigate(`/user/${rating.user_id}`);
            }}
            className="font-semibold text-white hover:text-amber-500 transition-colors"
          >
            {rating.username}
          </button>
          <span className="text-white/40 text-xs whitespace-nowrap">{formatDateTime(rating.created_at)}</span>
        </div>
        
        {/* Rating display */}
        <div className="flex items-center gap-2">
          <span className="text-white/70 text-sm">rated this cigar</span>
          <div className="flex items-center gap-1 bg-amber-500/20 px-2 py-1 rounded-full">
            <Star size={14} className="text-amber-500 fill-amber-500" />
            <span className="text-amber-500 font-bold text-sm">{rating.rating}/10</span>
          </div>
        </div>

        {/* Reply button */}
        <div className="flex items-center gap-4 mt-2">
          <button 
            onClick={() => {
              if (!token) {
                toast.error('Please sign in to reply');
                onNavigate('/auth');
                return;
              }
              const replyId = `rating-${rating.id}`;
              setReplyingTo(replyingTo === replyId ? null : replyId);
              setReplyText('');
            }}
            className="flex items-center gap-1 text-amber-500 text-sm hover:text-amber-400 transition-colors"
          >
            <MessageCircle size={14} />
            Reply
          </button>
        </div>

        {/* Reply input */}
        {replyingTo === `rating-${rating.id}` && (
          <div className="mt-3 flex gap-2">
            <Input
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder={`Reply to ${rating.username}'s rating...`}
              className="flex-1 bg-white/5 border-white/10 text-white placeholder:text-white/40 text-sm"
              onKeyPress={(e) => e.key === 'Enter' && onAddComment(`rating-${rating.id}`, replyText)}
              autoFocus
            />
            <Button 
              onClick={() => onAddComment(`rating-${rating.id}`, replyText)}
              className="bg-amber-600 hover:bg-amber-700 text-sm px-3"
              size="sm"
            >
              Reply
            </Button>
            <Button 
              onClick={() => {
                setReplyingTo(null);
                setReplyText('');
              }}
              variant="ghost"
              className="text-white/50 hover:text-white text-sm px-2"
              size="sm"
            >
              Cancel
            </Button>
          </div>
        )}
      </div>
    </div>

    {/* Render replies to this rating */}
    {replies && replies.length > 0 && (
      <div className="mt-3 ml-12 space-y-2">
        {replies.map((reply) => (
          <CommentCard 
            key={reply.id} 
            comment={reply} 
            depth={1}
            token={token}
            user={user}
            replyingTo={replyingTo}
            replyText={replyText}
            setReplyingTo={setReplyingTo}
            setReplyText={setReplyText}
            onAddComment={onAddComment}
            onDeleteComment={onDeleteComment}
            onNavigate={onNavigate}
          />
        ))}
      </div>
    )}
  </div>
);

const CommentCard = ({ 
  comment, 
  depth = 0, 
  token, 
  user, 
  replyingTo, 
  replyText, 
  setReplyingTo, 
  setReplyText, 
  onAddComment, 
  onDeleteComment,
  onNavigate 
}) => (
  <div className={`${depth > 0 ? 'ml-8 border-l-2 border-white/10 pl-4' : ''}`}>
    <div className="bg-white/5 rounded-xl p-4 mb-2">
      <div className="flex items-start gap-3">
        {/* Avatar */}
        {comment.profile_pic ? (
          <img 
            src={comment.profile_pic} 
            alt={comment.username}
            className={`${depth > 0 ? 'w-8 h-8' : 'w-12 h-12'} rounded-full object-cover flex-shrink-0`}
          />
        ) : (
          <div className={`${depth > 0 ? 'w-8 h-8 text-sm' : 'w-12 h-12 text-lg'} rounded-full bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center text-white font-bold flex-shrink-0`}>
            {comment.username?.charAt(0).toUpperCase() || 'U'}
          </div>
        )}
        
        {/* Content */}
        <div className="flex-1 min-w-0 overflow-hidden">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mb-1">
            <button 
              onClick={(e) => {
                e.stopPropagation();
                onNavigate(`/user/${comment.user_id}`);
              }}
              className="font-semibold text-white hover:text-amber-500 transition-colors truncate max-w-[150px]"
            >
              {comment.username}
            </button>
            <span className="text-white/40 text-xs whitespace-nowrap">{formatDateTime(comment.created_at)}</span>
          </div>
          
          {/* Show if comment was deleted */}
          {comment.deleted ? (
            <p className="text-white/30 italic">[deleted]</p>
          ) : (
            <p className="text-white/90 break-words">{comment.text}</p>
          )}
          
          <div className="flex items-center gap-4 mt-2">
            {/* Only show reply button if not at max depth and comment not deleted */}
            {depth < MAX_REPLY_DEPTH - 1 && !comment.deleted && (
              <button 
                onClick={() => {
                  if (!token) {
                    toast.error('Please sign in to reply');
                    onNavigate('/auth');
                    return;
                  }
                  setReplyingTo(replyingTo === comment.id ? null : comment.id);
                  setReplyText('');
                }}
                className="flex items-center gap-1 text-amber-500 text-sm hover:text-amber-400 transition-colors"
              >
                <MessageCircle size={14} />
                Reply
              </button>
            )}
            {/* Delete button - only show for user's own comments that aren't already deleted */}
            {user && comment.user_id === user.id && !comment.deleted && (
              <button 
                onClick={() => onDeleteComment(comment.id)}
                className="flex items-center gap-1 text-red-500 text-sm hover:text-red-400 transition-colors"
              >
                <Trash2 size={14} />
                Delete
              </button>
            )}
          </div>
          
          {/* Reply input */}
          {replyingTo === comment.id && (
            <div className="mt-3 flex gap-2">
              <Input
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder={`Reply to ${comment.username}...`}
                className="flex-1 bg-white/5 border-white/10 text-white placeholder:text-white/40 text-sm"
                onKeyPress={(e) => e.key === 'Enter' && onAddComment(comment.id, replyText)}
                autoFocus
              />
              <Button 
                onClick={() => onAddComment(comment.id, replyText)}
                className="bg-amber-600 hover:bg-amber-700 text-sm px-3"
                size="sm"
              >
                Reply
              </Button>
              <Button 
                onClick={() => {
                  setReplyingTo(null);
                  setReplyText('');
                }}
                variant="ghost"
                className="text-white/50 hover:text-white text-sm px-2"
                size="sm"
              >
                Cancel
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
    
    {/* Render replies */}
    {comment.replies && comment.replies.length > 0 && (
      <div className="mt-2">
        {comment.replies.map((reply) => (
          <CommentCard 
            key={reply.id} 
            comment={reply} 
            depth={depth + 1}
            token={token}
            user={user}
            replyingTo={replyingTo}
            replyText={replyText}
            setReplyingTo={setReplyingTo}
            setReplyText={setReplyText}
            onAddComment={onAddComment}
            onDeleteComment={onDeleteComment}
            onNavigate={onNavigate}
          />
        ))}
      </div>
    )}
  </div>
);

const DiscussionsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [comments, setComments] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [ratingComments, setRatingComments] = useState({});
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');

  const fetchComments = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/comments/cigar/${id}`);
      setComments(res.data);
    } catch (error) {
      console.error('Failed to fetch comments');
    }
  }, [id]);

  const fetchRatings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/ratings/cigar/${id}/with-users`);
      setRatings(res.data);
    } catch (error) {
      console.error('Failed to fetch ratings');
    }
  }, [id]);

  const fetchRatingComments = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/comments/cigar/${id}/by-rating`);
      setRatingComments(res.data);
    } catch (error) {
      console.error('Failed to fetch rating comments');
    }
  }, [id]);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      await Promise.all([fetchComments(), fetchRatings(), fetchRatingComments()]);
      setLoading(false);
    };
    fetchAll();
  }, [fetchComments, fetchRatings, fetchRatingComments]);

  const handleAddComment = async (parentId = null, text = null) => {
    if (!token) {
      toast.error('Please sign in to comment');
      navigate('/auth');
      return;
    }

    const commentText = text || newComment;
    if (!commentText.trim()) return;

    try {
      // Check if this is a reply to a rating (parentId starts with "rating-")
      const isRatingReply = parentId && parentId.startsWith('rating-');
      
      await axios.post(
        `${API}/comments`,
        { 
          cigar_id: id, 
          text: commentText, 
          parent_id: isRatingReply ? null : parentId,
          rating_id: isRatingReply ? parentId : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (parentId) {
        setReplyText('');
        setReplyingTo(null);
      } else {
        setNewComment('');
      }
      // Refetch both comments and rating comments
      fetchComments();
      fetchRatingComments();
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!token) return;

    try {
      await axios.delete(`${API}/comments/${commentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchComments();
      fetchRatingComments();
      toast.success('Comment deleted!');
    } catch (error) {
      toast.error('Failed to delete comment');
    }
  };

  return (
    <div className="h-screen bg-[#0B0B0C] flex flex-col overflow-hidden">
      {/* Header - Fixed at top */}
      <div className="sticky top-0 z-10 flex items-center p-4 border-b border-white/10 bg-[#0B0B0C]">
        <button onClick={() => navigate(-1)} className="text-white mr-4">
          <X size={24} />
        </button>
        <h1 className="playfair text-xl text-white flex-1 text-center pr-8">Comments/Ratings</h1>
      </div>

      {/* Combined List - Scrollable middle section */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {loading ? (
          <div className="text-center text-white/50 py-8">Loading...</div>
        ) : comments.length === 0 && ratings.length === 0 ? (
          <div className="text-center text-white/50 py-8">No activity yet. Be the first to rate or comment!</div>
        ) : (
          // Combine and sort by date (newest first)
          [...comments.map(c => ({...c, type: 'comment'})), ...ratings.map(r => ({...r, type: 'rating'}))]
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
            .map((item) => (
              item.type === 'rating' ? (
                <RatingCard 
                  key={`rating-${item.id}`} 
                  rating={item} 
                  onNavigate={navigate}
                  token={token}
                  user={user}
                  replyingTo={replyingTo}
                  replyText={replyText}
                  setReplyingTo={setReplyingTo}
                  setReplyText={setReplyText}
                  onAddComment={handleAddComment}
                  onDeleteComment={handleDeleteComment}
                  replies={ratingComments[item.id] || []}
                />
              ) : (
                <CommentCard 
                  key={`comment-${item.id}`} 
                  comment={item}
                  token={token}
                  user={user}
                  replyingTo={replyingTo}
                  replyText={replyText}
                  setReplyingTo={setReplyingTo}
                  setReplyText={setReplyText}
                  onAddComment={handleAddComment}
                  onDeleteComment={handleDeleteComment}
                  onNavigate={navigate}
                />
              )
            ))
        )}
      </div>

      {/* Comment Input Area - Fixed at bottom */}
      <div className="sticky bottom-0 z-10 border-t border-white/10 p-4 bg-[#0B0B0C]">
        {token ? (
          <div className="flex gap-2">
            <Input
              data-testid="comment-input"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Add a comment..."
              className="flex-1 bg-white/5 border-white/10 text-white placeholder:text-white/40"
              onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
            />
            <Button 
              data-testid="add-comment-button" 
              onClick={() => handleAddComment()} 
              className="bg-amber-600 hover:bg-amber-700"
            >
              Post
            </Button>
          </div>
        ) : (
          <button 
            onClick={() => navigate('/auth')}
            className="w-full py-4 rounded-xl border border-amber-600/50 text-amber-500 flex items-center justify-center gap-2 hover:bg-amber-600/10 transition-colors"
          >
            <LogOut size={18} />
            Sign in to comment
          </button>
        )}
      </div>
    </div>
  );
};

export default DiscussionsPage;
